import {
  assignChannelsQuery,
  Channel,
  DeleteWorkspaceUserMutation,
  ResendInviteWorkspaceMemberMutation,
  RevokeInvitationTokenMutation,
  RoleEnum,
  UpdateWorkspaceMemberMutation,
  workspaceMembersQuery,
  WorkspaceMembersV2Query,
} from '@/types/generated/graphql';
import { FetchResult } from '@apollo/client';
import { UseCursorPaginationReturn } from '@shared/hooks/use-cursor-pagination';
import React, { CSSProperties } from 'react';
import { Control, UseFormSetValue } from 'react-hook-form';

export const STATUS_TYPES = {
  ALL: 'all',
  ACTIVE: 'active',
  INVITES: 'invites',
} as const;

export type StatusType = (typeof STATUS_TYPES)[keyof typeof STATUS_TYPES];

export type MembersActiveTabProps = {
  requestEditMember: (existingRecord: TUpdateMemberForm) => void;
  requestAssignChannel: (memberId: number) => void;
  handleUpdateMember: ({
    id,
    role,
    email,
    name,
  }: TUpdateMemberForm) => Promise<FetchResult<UpdateWorkspaceMemberMutation>> | undefined;
  data: MembersViewProps['data'];
  requestDeleteMember: (id: number, name: string) => void;
  requestRevokeInvitation: (id: number, name: string) => void;
  requestResendInvitation: (email: string, name: string) => void;
  requestChangeRole: ({ id, name, email, role }: TUpdateMemberForm) => void;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  status: StatusType;
  requestManageNotifications: (id: number) => void;
};

export type MembersEdge = NonNullable<NonNullable<WorkspaceMembersV2Query['workspaceMembersV2']>['data']>[number];

export type TAssignChannelForm = {
  checkedChannel: number[];
  uncheckedChannel: number[];
  userWorkspaceId: number;
};

export type TUpdateMemberForm = {
  id: number;
  name: string;
  email: string;
  role: RoleEnum;
};

export type MembersViewProps = {
  status: StatusType;
  data: UseCursorPaginationReturn<WorkspaceMembersV2Query['workspaceMembersV2'] | undefined | null>;
  navigate: (path: string) => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  openEditModal: boolean;
  handleCloseEditModal: () => void;
  handleRequestUpdateMember: (existingRecord: TUpdateMemberForm) => void;
  editMemberLoading: boolean;
  handleUpdateSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  updateMemberCotrol: Control<TUpdateMemberForm>;
  openChannelModal: boolean;
  handleCloseAssignModal: () => void;
  assignChannelControl: Control<TAssignChannelForm>;
  handleRequestAssignChannel: (memberId: number) => void;
  handleAssignSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  assignMemberLoading: boolean;
  handleMemberUpdate: ({
    id,
    role,
    email,
    name,
  }: TUpdateMemberForm) => Promise<FetchResult<UpdateWorkspaceMemberMutation>> | undefined;
  availableChannels: assignChannelsQuery | undefined;
  setValue: UseFormSetValue<TAssignChannelForm>;
  channelSearchQuery: string;
  setChannelSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  deleteData: TDeleteData;
  deleteMemberLoading: boolean;
  handleDeleteMember: () => Promise<FetchResult<DeleteWorkspaceUserMutation>> | undefined;
  handleCancel: () => void;
  requiresFreeNumberDisposition: boolean;
  claimedFreeNumbers: ClaimedFreeNumber[];
  handleRemoveWithDisposition: (input: RemoveDispositionInput) => Promise<{ message: string }>;
  revokeData: TDeleteData;
  revokeInvitationLoading: boolean;
  handleRevokeInvitation: () => Promise<FetchResult<RevokeInvitationTokenMutation>> | undefined;
  requestDeleteMember: (id: number, name: string) => void;
  requestRevokeInvitation: (id: number, name: string) => void;
  resendData: TResendInviteData;
  requestResendInvitation: (email: string, name: string) => void;
  handleResendInvitation: () => Promise<FetchResult<ResendInviteWorkspaceMemberMutation>> | undefined;
  resendInvitationLoading: boolean;
  handleCloseModal: () => void;
  requestChangeRole: ({ id, name, email, role }: TUpdateMemberForm) => void;
  changeRoleData: TUpdateMemberForm | undefined | null;
  handleChangeRole: () => Promise<FetchResult<UpdateWorkspaceMemberMutation>> | undefined;
  handleCloseChangeRoleModal: VoidFunction;
  selectedChannelIds: Array<number>;
  assignedChannelIds: Array<number>;
  handleCheckboxGroupChange: (checkedValues: number[]) => void;
  userName: string;
  setUserName: React.Dispatch<React.SetStateAction<string>>;
  membersCount: number;
  membersCountAccepted: number;
  hasChanges: boolean;
};

export type UseMembersFn = () => MembersViewProps;

export type UpdateMembersModalProps = {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  control: Control<TUpdateMemberForm>;
  isOpen: boolean;
  closeModal: VoidFunction;
  loading: boolean;
  changeRoleData: TUpdateMemberForm | undefined | null;
  handleChangeRole: () => Promise<FetchResult<UpdateWorkspaceMemberMutation>> | undefined;
  handleCancel: VoidFunction;
};

export type DeleteMembersModalProps = {
  deleteData: TDeleteData;
  deleteMemberLoading: boolean;
  handleDeleteMember: () => Promise<FetchResult<DeleteWorkspaceUserMutation>> | undefined;
  handleCancel: () => void;
  requiresFreeNumberDisposition: boolean;
  claimedFreeNumbers: ClaimedFreeNumber[];
  handleRemoveWithDisposition: (input: RemoveDispositionInput) => Promise<{ message: string }>;
};

export type InvitationModalProps = {
  resendData: TResendInviteData;
  handleResendInvitation: () => Promise<FetchResult<ResendInviteWorkspaceMemberMutation>> | undefined;
  resendInvitationLoading: boolean;
  revokeData: TDeleteData;
  revokeInvitationLoading: boolean;
  handleRevokeInvitation: () => Promise<FetchResult<RevokeInvitationTokenMutation>> | undefined;
  handleCloseModal: () => void;
};

export type TAssignedChannel = NonNullable<
  NonNullable<
    NonNullable<NonNullable<NonNullable<workspaceMembersQuery['workspaceMembers']>['edges']>[number]>['node']
  >['channels']
>;

export type AssignChanelModalProps = {
  onSubmit: (e?: React.BaseSyntheticEvent) => Promise<void>;
  control: Control<TAssignChannelForm>;
  isOpen: boolean;
  closeModal: VoidFunction;
  loading: boolean;
  name: string;
  availableChannels: assignChannelsQuery | undefined;
  channelSearchQuery: string;
  setChannelSearchQuery: React.Dispatch<React.SetStateAction<string>>;

  selectedChannelIds: Array<number>;
  handleCheckboxGroupChange: (checkedValues: number[]) => void;
  hasChanges: boolean;
};

export type TDeleteData = { id: number; name: string } | null | undefined;

export type ClaimedFreeNumber = {
  id: string;
  phoneNumber: string;
  friendlyName: string;
  price?: string; // cents, string — matches existing basePrice/currentPrice convention
  countryFlag?: string; // flag image URL — matches Channel.country.flag convention
};

export type RemoveDispositionInput = {
  channelId: string;
  disposition: 'delete' | 'purchase';
  // Gateway account of the selected payment card — sent only when disposition is 'purchase'.
  gatewayAccountId?: string;
};

export type TChangeRoleData = {
  id: number;
  name: string;
  email: string;
  role: RoleEnum;
};

export type TResendInviteData = { email: string; name: string } | null | undefined;

export type TabBadgeProps = {
  count: number;
  style?: CSSProperties;
};

export type ModalManageAssignedNumbersProps = {
  open: boolean;
  onClose: VoidFunction;
  onToggle: (channelId: number) => void;
  availableChannels: assignChannelsQuery | undefined;
  channelSearchQuery: string;
  setChannelSearchQuery: React.Dispatch<React.SetStateAction<string>>;
  channelsState: Channel[];
  setChannelsState: React.Dispatch<React.SetStateAction<Channel[]>>;
};
export type TManageNotificationsData = {
  userId: number;
};
